import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SendWhatsAppOptions {
  to: string;
  message: string;
  leadId?: string;
  contactId?: string;
  dealId?: string;
}

interface SendWhatsAppTemplateOptions {
  to: string;
  templateName: string;
  templateLanguage?: string;
  templateComponents?: any[];
  leadId?: string;
  contactId?: string;
  dealId?: string;
}

export function useWhatsApp() {
  const [isSending, setIsSending] = useState(false);

  const sendMessage = async (options: SendWhatsAppOptions) => {
    setIsSending(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase.functions.invoke('send-whatsapp', {
        body: {
          ...options,
          tenantId: profile.tenant_id,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('WhatsApp message sent successfully');
        return data;
      } else {
        throw new Error(data?.error || 'Failed to send message');
      }
    } catch (err: any) {
      console.error('WhatsApp send error:', err);
      toast.error(err.message || 'Failed to send WhatsApp message');
      throw err;
    } finally {
      setIsSending(false);
    }
  };

  const sendTemplate = async (options: SendWhatsAppTemplateOptions) => {
    setIsSending(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase.functions.invoke('send-whatsapp', {
        body: {
          to: options.to,
          templateName: options.templateName,
          templateLanguage: options.templateLanguage,
          templateComponents: options.templateComponents,
          tenantId: profile.tenant_id,
          leadId: options.leadId,
          contactId: options.contactId,
          dealId: options.dealId,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('WhatsApp template sent successfully');
        return data;
      } else {
        throw new Error(data?.error || 'Failed to send template');
      }
    } catch (err: any) {
      console.error('WhatsApp template send error:', err);
      toast.error(err.message || 'Failed to send WhatsApp template');
      throw err;
    } finally {
      setIsSending(false);
    }
  };

  return {
    isSending,
    sendMessage,
    sendTemplate,
  };
}
